import moment from 'moment';
import React, { useState, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataTable from '../collection/DataTable';
import TableHandler from '../collection/TableHandler';
import BoolIcon from '../collection/icons/Boolean';
import BsButton from '../collection/bootstrap/BsButton';
import ValidacionFilter from '../components/filters/ValidacionFilter';
import FicharModal from '../components/modals/FicharModal';
import FloatingAddButton from '../components/project/FloatingAddButton';
import { apiDelete, apiForm, getSession, apiBlobForm } from '../utils/api';
import { generateFormData, sortableString } from '../utils/functions';
import { swalertConfirm } from '../utils/notifications';

export default function Fichajes(props) {
    const params = useParams();

    // default filter
    const usuario = getSession();
    let desde = params.mes ? moment(params.mes, "YYYY-MM") : moment();
    let hasta = params.mes ? moment(params.mes, "YYYY-MM") : moment();
    desde = params.desde ? moment(params.desde, "YYYY-MM-DD") : desde.startOf('month').startOf('week');
    hasta = params.hasta ? moment(params.hasta, "YYYY-MM-DD") : hasta.endOf('month').endOf('week');
    const defaultFilter = {
        desde: desde.format("YYYY-MM-DD"),
        hasta: null,
        trabajador: params.idTrabajador ? params.idTrabajador : (props.super ? null : usuario.id),
        departamentos: props.super ? null : [0]
    };
    const filter = useRef(generateFormData(defaultFilter));

    // show excel button
    const [showExcelButton, setShowExcelButton] = useState(false);

    // api calls
    const updateData = async newFilter => {
        const actualFilter = newFilter ?? filter.current;
        filter.current = actualFilter;
        newFilter ? setShowExcelButton(true) : setShowExcelButton(false);
        return await apiForm("fichajes/table", actualFilter);
    };
    const deleteRow = async id => await apiDelete("fichajes/delete/" + id);

    // pdf button
    const handleListoParaFirmar = async () => {
        const ok = await swalertConfirm(
            "¿Confirma que el listado de fichajes es correcto?",
            "El número de fichajes de cada semana deber ser par, siempre que hay una entrada debe haber una salida.");
        if (ok) {
            const formData = new FormData();
            formData.append("trabajador", params.idTrabajador);
            formData.append("mes", params.mes);
            apiForm("pdfs/readytosign", formData);
        }
    }
    const btnListoParaFirmar = <div className="d-flex justify-content-center gap-3 my-3">
        <Button variant="success" onClick={handleListoParaFirmar}>Listo para firmar</Button>
    </div>;

    // excel download
    const downloadExcel = async () => {
        const blob = await apiBlobForm("fichajes/excel", filter.current);
        const url = window.URL.createObjectURL(blob);

        // download file
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Fichajes_' + filter.current.get("desde") + '_' + filter.current.get("hasta") + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // render
    return <>
        <h2>{props.super ? (props.pdfbtn ? "Revisar fichajes del mes" : "Supervisar fichajes") : "Mis fichajes"}</h2>
        <TableHandler
            onUpdate={updateData}
            onDelete={deleteRow}
            elementText="el fichaje">
            {props.pdfbtn ? btnListoParaFirmar : (
                <ValidacionFilter
                    className="my-3" fem
                    defaultValue={defaultFilter}
                    selectFechas
                    selectDepartamento={props.super && (usuario.permisoValidacion || usuario.permisoAdmin)}
                    selectTrabajador={props.super} />
            )}
            {(usuario.permisoAdmin || usuario.permisoValidacion) && showExcelButton && <BsButton className="mb-2" onClick={downloadExcel}>Descargar Excel</BsButton>}
            <FichajesTable
                super={props.super}
                trabajador={params.idTrabajador}
                mes={params.mes} />
        </TableHandler>
    </>;
}

function FichajesTable(props) {

    // table columns
    const columns = [
        {
            name: "Trabajador",
            selector: row => sortableString(row.NombreTrabajador),
            format: row => row.NombreTrabajador,
            sortable: true,
            omit: !props.super
        },
        {
            name: "Fecha",
            selector: row => moment.utc(row.FechaHora).local().format("YYYY-MM-DD HH:mm"),
            format: row => moment.utc(row.FechaHora).local().format("DD/MM/YY"),
            sortable: true,
            width: '10%'
        },
        {
            name: "Hora",
            selector: row => moment.utc(row.FechaHora).local().format("HH:mm"),
            sortable: true,
            width:'10%'
        },
        {
            name: "Tipo",
            selector: row => row.FromApp,
            format: row => row.FromApp ? "Tarjeta" : "Web",
            sortable: true,
            center: true,
            width: '10%'
        },
        {
            name: "Dieta",
            selector: row => row.Dieta,
            format: row => <BoolIcon value={row.Dieta} size="lg" />,
            sortable: true,
            center: true,
            width:'8%'
        },
        {
            name: "Comentario",
            selector: row => sortableString(row.Comentario),
            format: row => row.Comentario,
            sortable: true
        }
    ];
    // table buttons
    const buttons = <>
        <Button variant="secondary" size="sm" onClick={props.onEdit}><FontAwesomeIcon icon={faEdit} /></Button>
        {props.super ? <Button variant="danger" size="sm" onClick={props.onDelete}><FontAwesomeIcon icon={faTrash} /></Button> : ""}
    </>;
    const name = row => (props.super && (row.NombreTrabajador + " - ")) + moment.utc(row.FechaHora).local().format("HH:mm DD/MM/YY");

    // render
    return <>
        <DataTable responsive rowname={name} columns={columns} data={props.data} buttons={buttons} pagination defaultSortFieldId={2} defaultSortAsc={false} />
        {props.formOpen && (
            <FicharModal
                show={props.formOpen}
                onHide={props.onFormClose}
                idFichaje={props.formId}
                admin={props.admin}
                super={props.super}
                defaultTrabajador={props.trabajador}
                defaultFecha={props.mes ? props.mes : ""}
            />
        )}
        <FloatingAddButton onClick={props.onNew} />
    </>;
}
